import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { safeHttpUrl } from "@/lib/url";
import { PageHeader } from "@/components/common/DashboardBits";
import PincodeInput from "@/components/common/PincodeInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, KeyRound, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import BankReviewCell from "@/components/bank/BankReviewCell";

export default function BanksPage() {
  const { t } = useLang();
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUser, setOpenUser] = useState(null); // bank object for create user

  const load = () => {
    api.get("/banks").then((r) => setBanks(r.data));
  };
  useEffect(() => { load(); }, []);
  const pending = banks.filter((b) => !b.is_approved).length;
  const replaceBank = (saved) => setBanks((list) => list.map((b) => (b.id === saved.id ? saved : b)));

  return (
    <div>
      <PageHeader
        title={t("banks")}
        subtitle={`${banks.length} registered${pending ? ` · ${pending} awaiting approval` : ""}`}
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-bank-button">
                <Plus className="h-4 w-4 mr-1" /> {t("add_bank")}
              </Button>
            </DialogTrigger>
            <BankFormDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Portal</TableHead>
                <TableHead>Approval</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {banks.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No banks yet.</TableCell></TableRow>
              )}
              {banks.map((b) => (
                <TableRow key={b.id} data-testid={`bank-row-${b.id}`} className={b.is_approved ? "" : "bg-amber-50/40"}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="text-slate-600">{b.branch}</TableCell>
                  <TableCell className="text-slate-600">{b.location}</TableCell>
                  <TableCell className="text-slate-600 text-xs">{b.contact_email}<br/>{b.contact_phone}</TableCell>
                  <TableCell>
                    {safeHttpUrl(b.portal_link) ? (
                      <a href={safeHttpUrl(b.portal_link)} target="_blank" rel="noreferrer noopener" className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1">
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell><BankReviewCell bank={b} onChanged={replaceBank} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenUser(b)} data-testid={`bank-create-user-${b.id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm("Delete bank?")) return;
                        try { await api.delete(`/banks/${b.id}`); toast.success("Deleted"); load(); }
                        catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`bank-delete-${b.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {openUser && (
        <CreateUserDialog
          bank={openUser}
          role="bank"
          onClose={() => setOpenUser(null)}
          onSaved={() => setOpenUser(null)}
        />
      )}
    </div>
  );
}

function BankFormDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", branch: "", registered_person: "", contact_email: "", contact_phone: "",
    agreement_terms: "", payment_terms: "", portal_link: "", address: "", location: "",
    city: "", pincode: "",
  });
  const [busy, setBusy] = useState(false);
  const [pincodeOk, setPincodeOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.pincode && !pincodeOk) {
      toast.error("Please enter a valid Rajasthan pincode");
      return;
    }
    setBusy(true);
    try {
      await api.post("/banks", form);
      toast.success("Bank added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">Add Bank / NBFC</DialogTitle>
        <DialogDescription>Register a bank with agreement and portal details.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><Label>Bank Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bank-name" /></div>
        <div><Label>Branch</Label><Input required value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} data-testid="bank-branch" /></div>
        <PincodeInput
          value={form.pincode}
          onChange={(p) => { setForm({ ...form, pincode: p }); setPincodeOk(false); }}
          onResolved={(r) => {
            setPincodeOk(r.isRajasthan);
            if (r.isRajasthan) setForm({ ...form, pincode: r.pincode, city: r.city, location: r.district });
          }}
          testId="bank-pincode"
          label="Pincode"
        />
        <div><Label>City / Post Office</Label><Input value={form.city} readOnly className="bg-slate-50" data-testid="bank-city" /></div>
        <div><Label>District</Label><Input value={form.location} readOnly className="bg-slate-50" data-testid="bank-district" /></div>
        <div><Label>Registered Person</Label><Input value={form.registered_person} onChange={(e) => setForm({ ...form, registered_person: e.target.value })} data-testid="bank-person" /></div>
        <div><Label>Contact Email</Label><Input type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="bank-email" /></div>
        <div><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="bank-phone" /></div>
        <div className="md:col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="bank-address" /></div>
        <div><Label>Agreement Terms</Label><Textarea rows={2} value={form.agreement_terms} onChange={(e) => setForm({ ...form, agreement_terms: e.target.value })} data-testid="bank-agreement" /></div>
        <div><Label>Payment Terms</Label><Textarea rows={2} value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="bank-payment" /></div>
        <div className="md:col-span-2"><Label>Valuation Portal Link</Label><Input placeholder="https://valuation.bank.co.in" value={form.portal_link} onChange={(e) => setForm({ ...form, portal_link: e.target.value })} data-testid="bank-portal" /></div>
        <DialogFooter className="md:col-span-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="bg-slate-950 hover:bg-slate-800" disabled={busy} data-testid="bank-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function CreateUserDialog({ bank, role, onClose, onSaved }) {
  const [form, setForm] = useState({ email: bank?.contact_email || "", password: "", name: bank?.name || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.toLowerCase(),
        password: form.password,
        name: form.name,
        role,
        entity_id: bank.id,
        phone: bank.contact_phone,
      });
      toast.success("Login user created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Bank Login</DialogTitle>
          <DialogDescription>Grant this bank access to their dashboard.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary Password</Label><Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-slate-950 hover:bg-slate-800" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
